import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary, Notice } from '@kollez/design-system';

import { useGoverningEntity, type GoverningEntityStatus } from '@/lib/queries';

/**
 * /group/governing-entities/:id — one Trust/Society/Section-8/Government
 * legal entity, with its registration facts and the list of colleges +
 * universities that legally sit under it. Read-only from the tenant side —
 * platform ops writes the master row when the entity is registered.
 */

const TYPE_LABEL: Record<string, string> = {
  Trust: 'Trust',
  Society: 'Society',
  Section_8_Company: 'Section-8 company',
  Government: 'Government',
  Autonomous_Body: 'Autonomous body',
  Other: 'Other',
};

const STATUS_LABEL: Record<GoverningEntityStatus, string> = {
  Active: 'Active', Inactive: 'Inactive', Suspended: 'Suspended', Dissolved: 'Dissolved',
};

function statusTone(s: GoverningEntityStatus): string {
  if (s === 'Active') return 'badge-success';
  if (s === 'Suspended') return 'badge-warning';
  if (s === 'Dissolved') return 'badge-danger';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function GroupGoverningEntityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useGoverningEntity(id);

  const e = data?.entity;
  const linked = data?.linked;
  const totalLinked = (linked?.colleges.length ?? 0) + (linked?.universities.length ?? 0);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link className="link" to="/group/governing-entities">← All governing entities</Link>
          </p>
          <h1 className="h-page">{e?.entityName ?? 'Governing entity'}</h1>
          {e && (
            <p className="subhead">
              {TYPE_LABEL[e.entityType] ?? e.entityType}{' '}
              · <span className={`badge ${statusTone(e.status)}`}>{STATUS_LABEL[e.status]}</span>
              {e.registrationNumber && <span className="ml-2">Reg. {e.registrationNumber}</span>}
            </p>
          )}
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={isLoading}
        error={error}
        empty={!isLoading && !e}
        emptyTitle="Entity not found"
        emptyHint="It may have been deleted or belongs to another tenant."
        onRetry={() => void refetch()}
      >
        {e && (
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="card stack lg:col-span-2">
              <h2 className="h-section">Registration</h2>
              <dl className="dl">
                <dt>Entity name</dt><dd>{e.entityName}</dd>
                <dt>Type</dt><dd>{TYPE_LABEL[e.entityType] ?? e.entityType}</dd>
                <dt>Registration number</dt><dd>{e.registrationNumber ?? '—'}</dd>
                <dt>PAN</dt><dd>{e.panNumber ?? '—'}</dd>
                <dt>Registered on</dt><dd>{fmt(e.registeredAt)}</dd>
                <dt>Address</dt>
                <dd>
                  {e.address
                    ? [e.address.line1, e.address.line2, e.address.city, e.address.state, e.address.pincode]
                        .filter(Boolean).join(', ')
                    : [e.city, e.state].filter(Boolean).join(', ') || '—'}
                </dd>
                {e.contactEmail && (<><dt>Contact email</dt><dd>{e.contactEmail}</dd></>)}
                {e.contactPhone && (<><dt>Contact phone</dt><dd>{e.contactPhone}</dd></>)}
                {e.remarks && (<><dt>Remarks</dt><dd>{e.remarks}</dd></>)}
              </dl>

              <Notice tone="info">
                <b>Registration number is the source of truth.</b> If it changes (transfer, new MoA),
                a new entity is recorded and this one gets marked dissolved — the number is never
                edited in place.
              </Notice>
            </div>

            <div className="card stack">
              <h2 className="h-section">
                Legally owns{' '}
                <span className="helper" style={{ fontWeight: 400 }}>({totalLinked})</span>
              </h2>

              {linked && linked.universities.length > 0 && (
                <div>
                  <p className="eyebrow">Universities</p>
                  <ul className="stack">
                    {linked.universities.map((u) => (
                      <li key={u._id}>
                        <div className="cell-title">{u.name}</div>
                        {u.type && <div className="cell-sub">{u.type}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {linked && linked.colleges.length > 0 && (
                <div>
                  <p className="eyebrow">Colleges</p>
                  <ul className="stack">
                    {linked.colleges.map((c) => (
                      <li key={c._id}>
                        <Link className="cell-title" to={`/group/colleges/${c._id}`}>{c.name}</Link>
                        <div className="cell-sub">
                          {c.type ?? '—'}{c.code ? ` · ${c.code}` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {totalLinked === 0 && (
                <p className="helper">
                  No colleges or universities cite this entity. If that's wrong, ask platform ops
                  to backfill the governingEntity ref on the affected institutions.
                </p>
              )}
            </div>
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}
